import { emojiDefaults } from "@/components/emoji/emoji.type.ts";
import {
  SidebarPanelItem,
  useModals,
  parseUrlHelper,
  Box,
  ButtonIcon,
  FontAwesomeIcon,
  SidebarCollapseButton,
  Skeleton,
  Stack,
  Title,
} from "@skailer-com/uikit";
import { SidebarConfigItem } from "@/components/sidebar/sidebar-service.tsx";
import { createUrlWithParams } from "@/helpers/utils.helper.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { JobViewModal } from "@/pages/recruit/jobs/widgets/JobViewModal.tsx";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import { useJobDetails } from "@/queries/recruit/use-jobs.query.ts";
import { getStageEmoji } from "@/types/recruit/stages.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

export const SidebarRecruitmentJobDetails = () => {
  const { t } = useTranslation();
  const { canIEdit } = usePermissions();
  const { openModal } = useModals();

  const { data: employee } = useEmployeeMe();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const stageIds = parseUrlHelper.getNumberArray(searchParams, "stage_id");
  const jobId = parseUrlHelper.getNumberArray(searchParams, "job_id");
  const disqualified = searchParams.get("disqualified") == "true";
  const reserved = searchParams.get("reserved") == "true";
  const isAIRecommendations = location.pathname == "/recruitment/candidates/ai-recommendations";

  const jobDetails = useJobDetails(jobId && jobId.length == 1 ? jobId[0] : undefined);

  const currentJobPipelines = useMemo(() => {
    if (!jobDetails.data) return undefined;

    const result: SidebarConfigItem[] = [];

    const isAllCandidatesActive =
      (!stageIds || stageIds.length != 1) && !disqualified && !reserved && !isAIRecommendations;
    result.push({
      title: t("ats:candidates_all_active"),
      to: isAllCandidatesActive
        ? `/recruitment/candidates?job_id=${jobDetails.data.id}`
        : createUrlWithParams(searchParams, "/recruitment/candidates", {
            job_id: jobDetails.data.id.toString(),
            stage_id: undefined,
            disqualified: undefined,
            reserved: undefined,
          }),
      isActive:
        (!stageIds || stageIds.length != 1) && !disqualified && !reserved && !isAIRecommendations,
      count:
        jobDetails.data.pipeline.stages.reduce((acc, stage) => {
          acc = acc + stage.applicants_count;
          return acc;
        }, 0) || 0,
    });

    for (let i = 0; i < jobDetails.data.pipeline.stages.length; i++) {
      const stage = jobDetails.data.pipeline.stages[i];
      result.push({
        title: stage.title,
        to: createUrlWithParams(searchParams, "/recruitment/candidates", {
          job_id: jobDetails.data.id.toString(),
          stage_id: stage.id.toString(),
          disqualified: undefined,
          reserved: undefined,
        }),
        isActive: (stageIds.length == 1 && stageIds.indexOf(stage.id) !== -1) || false,
        count: stage.applicants_count,
        emoji: getStageEmoji(stage),
      });
    }

    if (jobDetails.data.pipeline.disqualified_candidates_count > 0) {
      result.push({
        title: t("ats:disqualified"),
        to: createUrlWithParams(searchParams, "/recruitment/candidates", {
          job_id: jobDetails.data.id.toString(),
          stage_id: undefined,
          disqualified: "true",
          reserved: undefined,
        }),
        isActive: disqualified || false,
        count: jobDetails.data.pipeline.disqualified_candidates_count,
        emoji: emojiDefaults.stages.disqualified,
      });
    }

    if (jobDetails.data.pipeline.reserved_candidates_count > 0) {
      result.push({
        title: t("ats:reserved"),
        to: createUrlWithParams(searchParams, "/recruitment/candidates", {
          job_id: jobDetails.data.id.toString(),
          stage_id: undefined,
          disqualified: undefined,
          reserved: "true",
        }),
        isActive: reserved || false,
        count: jobDetails.data.pipeline.reserved_candidates_count,
        emoji: emojiDefaults.stages.reserved,
      });
    }

    return result;
  }, [jobDetails.data, stageIds, employee, searchParams]);

  const canEditJob = employee && canIEdit(ApiPermission.manage_jobs);
  const [showPipeline, setShowPipeline] = useState(true);

  return (
    <>
      <Stack
        className={"h-16 flex items-center px-6"}
        direction={"horizontal"}
        gap={"sm"}
        justify={"between"}
        items={"center"}
      >
        <ButtonIcon
          onClick={() => {
            // const currentUrlParams = new URLSearchParams(window.location.search);
            // currentUrlParams.delete("job_id");
            navigate("/recruitment/candidates");
          }}
          variant="secondary"
          icon={<FontAwesomeIcon icon="fa-light fa-arrow-left" className={"text-lg"} />}
        />
        <Stack direction={"horizontal"} gap={"sm"}>
          {jobDetails.data && (
            <ButtonIcon
              icon={<FontAwesomeIcon icon="fa-light fa-circle-info" />}
              variant={"secondary"}
              onClick={() => {
                if (jobDetails.data) {
                  openModal(JobViewModal, { job: jobDetails.data });
                }
              }}
            />
          )}
          <SidebarCollapseButton variant={"secondary"} />
        </Stack>
      </Stack>
      <Stack gap={"lg"} className={"pb-4"}>
        {jobDetails.isLoading && (
          <Box className={"px-4"}>
            <Skeleton className={"w-full h-6"}></Skeleton>
          </Box>
        )}
        {jobDetails.data && (
          <Title header={`${jobDetails.data.title}`} size={"md"} className={"px-6"} />
        )}

        <Stack className={"px-4"}>
          {currentJobPipelines && (
            <>
              <Stack
                className={"pl-2 pb-1"}
                justify={"between"}
                direction={"horizontal"}
                items={"center"}
              >
                <Title header={t("ats:pipeline")} size={"xs"} />
                <ButtonIcon
                  icon={
                    <FontAwesomeIcon
                      icon={"fa-light " + (showPipeline ? "fa-chevron-down" : "fa-chevron-up")}
                    />
                  }
                  onClick={() => setShowPipeline(!showPipeline)}
                  variant={"plain"}
                />
              </Stack>
              {showPipeline && (
                <Stack gap={"xs"}>
                  {currentJobPipelines.map((item, index) => (
                    <SidebarPanelItem
                      key={`secondary-menu-pipeline-stage-${index}`}
                      to={item.to}
                      isActive={item.isActive}
                      title={item.title}
                      count={item.count}
                    />
                  ))}
                </Stack>
              )}
            </>
          )}
        </Stack>
        {canEditJob && jobDetails.data && (
          <Box className={"px-4"}>
            <SidebarPanelItem
              icon={<FontAwesomeIcon icon="fa-light fa-pen-to-square" />}
              isActive={false}
              title={t("ats:edit_job")}
              onClick={() => {
                sessionStorage.setItem("job_edit_referrer", location.pathname + location.search);
              }}
              to={`/recruitment/jobs/${jobDetails.data.id}/edit`}
            />
          </Box>
        )}
      </Stack>
    </>
  );
};
